import {
  MutationFullUpdateOrderArgs,
  ShipmentStopType,
  Stop,
  useFullUpdateOrderMutation,
} from "@api/graphql/generated/generated-types";
import { EditOrderShipment } from "../components/EditOrderModalComponent/hooks/useEditOrderShipment";
import { Dispatch, SetStateAction, useState } from "react";
import moment from "moment";
import { adjustTimeWindow } from "../utils/stopTimeStatusDisplayer";
import { getFirstTaskSequence } from "../utils/typeConvertors";
import { useCourieStore } from "@src/common/lib/store";
import { FieldValues, UseFormReset } from "react-hook-form";
import { useShipmentsCoreDataContextOptional } from "../context/ShipmentsCoreData";
import { EditOrderModalSlides } from "../components/EditOrderModalComponent/EditOrderModal";
import { useAuth } from "@src/common/hooks/useAuth";
import { findGooglePlaceId, findLocationId } from "../utils/stopFieldFinders";
import { resequenceStopsOnEdit } from "../utils/resequenceStopsOnEdit";
import { datePickerDateToISODate } from "@src/common/utils/timezoneConverters";
import { buildCustomFieldValues } from "@src/common/utils/customFieldValueMapper";
import { buildEditOrderPackageUpdate } from "../utils/buildEditOrderPackageUpdate";
import {
  StopOrderEditing,
  getStopOrderSave,
  makeStopOrderHint,
  shouldOpenConfirmRoute,
  useGuardedSubmission,
} from "@src/shipments/features/stopReordering";

interface EditOrderFormSubmitProps {
  onConfirm: () => void;
  reset: UseFormReset<FieldValues>;
  setCurrentSlide: Dispatch<SetStateAction<EditOrderModalSlides>>;
  isDifferentStopDate: boolean;
  shipment: EditOrderShipment;
  stopOrderEditing?: StopOrderEditing;
  refetchShipment?: () => Promise<unknown>;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

const useEditOrderFormSubmit = ({
  onConfirm,
  reset,
  setCurrentSlide,
  isDifferentStopDate,
  shipment,
  stopOrderEditing,
  refetchShipment,
  onSubmittingChange,
}: EditOrderFormSubmitProps) => {
  const { courieUser, employeeId } = useAuth();
  const { showToast } = useCourieStore();
  const shipmentsCoreData = useShipmentsCoreDataContextOptional();
  const refetchGetShipments = shipmentsCoreData?.refetchGetShipments;
  const [fullUpdateOrderMutation, { loading }] = useFullUpdateOrderMutation();
  const [stopIdsToDelete, setStopIdsToDelete] = useState<string[]>([]);
  const {
    isSubmitting,
    isRunning,
    runSubmission,
    rememberContinuation,
    retryContinuation,
  } = useGuardedSubmission({ onSubmittingChange });

  const onSubmit = (data: any) => {
    if (isRunning()) return;
    const retry = retryContinuation(data);
    if (retry) return retry;
    data = data as MutationFullUpdateOrderArgs;
    if (!shipment) return;

    const endCustomerId = data.endCustomerId;
    const courierId = data.courierId;
    const orderId = data.id;
    const costCenterId = data.costCenterId;
    const serviceTypeid = data.serviceType?.id;
    const shipmentDate = datePickerDateToISODate(data.shipmentDate);
    const { submittedStopOrderItems, hasStopOrderChange, shouldSendEntryNumbers } =
      getStopOrderSave({
        formStops: data.stops,
        savedStops: shipment.stops ?? [],
        stopOrderEditing,
      });
    const seqPerFormIndex = hasStopOrderChange
      ? null
      : resequenceStopsOnEdit(data.stops, shipment.stops ?? []);
    const { updatedStops, stopsToCreate } = data.stops.reduce(
      (acc, stop, index) => {
        if (stop.id) {
          const {
            streetAddress,
            deliverByDate,
            pickupByDate,
            lngLat,
            timeWindow,
            ...rest
          } = stop;
          // For delivery stops, use deliverByDate; for pickup stops, use pickupByDate
          const isDeliveryDateAfterShipmentDate =
            stop.deliverByDate &&
            moment(stop.deliverByDate).isAfter(moment(shipmentDate));
          const isPickupDateAfterShipmentDate =
            stop.pickupByDate &&
            moment(stop.pickupByDate).isAfter(moment(shipmentDate));

          // Determine which date to use for time window adjustment
          let dateForTimeWindow = shipmentDate;
          if (
            stop.type === ShipmentStopType.PickUp &&
            isPickupDateAfterShipmentDate
          ) {
            dateForTimeWindow = datePickerDateToISODate(stop.pickupByDate);
          } else if (
            stop.type !== ShipmentStopType.PickUp &&
            isDeliveryDateAfterShipmentDate
          ) {
            dateForTimeWindow = datePickerDateToISODate(stop.deliverByDate);
          }

          const adjustedTimeWindow = adjustTimeWindow(
            timeWindow,
            dateForTimeWindow,
          );
          const locationId = findLocationId(stop, shipment);
          const googlePlaceId = findGooglePlaceId(stop, shipment);
          const seq = seqPerFormIndex ? seqPerFormIndex[index] : undefined;
          acc.updatedStops.push({
            ...rest,
            timeWindow: adjustedTimeWindow,
            locationId: googlePlaceId ? undefined : locationId || null,
            googlePlaceId: googlePlaceId || undefined,
            stopSequenceNumber: seq,
            orderEntrySequenceNumber: shouldSendEntryNumbers
              ? index + 1
              : undefined,
          });
        } else {
          const { deliverByDate, pickupByDate, timeWindow, lngLat, ...rest } =
            stop;
          // For new stops, determine date for time window adjustment
          let dateForTimeWindow = shipmentDate;
          if (stop.type === ShipmentStopType.PickUp && stop.pickupByDate) {
            dateForTimeWindow = datePickerDateToISODate(stop.pickupByDate);
          } else if (stop.deliverByDate) {
            dateForTimeWindow = datePickerDateToISODate(stop.deliverByDate);
          }

          const adjustedTimeWindow = adjustTimeWindow(
            timeWindow,
            dateForTimeWindow,
          );
          const seq = seqPerFormIndex ? seqPerFormIndex[index] : undefined;
          acc.stopsToCreate.push({
            ...rest,
            timeWindow: adjustedTimeWindow,
            orderEntrySequenceNumber: shouldSendEntryNumbers ? index + 1 : seq,
          });
        }
        return acc;
      },
      { updatedStops: [], stopsToCreate: [] },
    );

    let submissionStops = updatedStops
      .sort((a: Stop, b: Stop) => {
        const taskSequenceDifference =
          getFirstTaskSequence(a) - getFirstTaskSequence(b);
        if (taskSequenceDifference !== 0) return taskSequenceDifference;
        if (
          a.type === ShipmentStopType.PickUp &&
          b.type !== ShipmentStopType.PickUp
        )
          return -1;
        if (
          a.type !== ShipmentStopType.PickUp &&
          b.type === ShipmentStopType.PickUp
        )
          return 1;
        return 0;
      })
      .map((stop) => {
        return {
          ...stop,
          stopDate: stop.stopDate
            ? datePickerDateToISODate(stop.stopDate)
            : undefined,
        };
      });
    let stopIdsToDeleteOnSubmit = stopIdsToDelete;
    const copyOfStopsToCreate = [...stopsToCreate];

    copyOfStopsToCreate.map((stop) => {
      stop.stopDate = stop.stopDate
        ? datePickerDateToISODate(stop.stopDate)
        : undefined;
      return stop;
    });

    if (endCustomerId && courierId && orderId && shipment) {
      const customFieldValues = buildCustomFieldValues(
        data.customFieldValues ?? {},
        data._customFieldDefs ?? [],
      );
      const packageUpdate = buildEditOrderPackageUpdate({
        packageItems: data.packageItems,
        packageSize: data.packageSize,
        packageTypeOrSizeMode: data.packageTypeOrSizeMode ?? "none",
      });

      const completePostMutation = () =>
        Promise.resolve(
          hasStopOrderChange &&
          stopsToCreate.length > 0 &&
          shipment.driver &&
          refetchShipment
            ? refetchShipment()
            : undefined
        ).then(() => {
          refetchGetShipments?.();
          showToast({
            type: "success",
            message: "Order updated successfully",
          });
          stopOrderEditing?.setStopOrderHint(
            hasStopOrderChange
              ? makeStopOrderHint(submittedStopOrderItems)
              : null
          );

          const isRouteChangeNeeded = shouldOpenConfirmRoute({
            hasDriver: !!shipment.driver,
            status: shipment.status,
            hasNewStop: stopsToCreate.length > 0,
            hasStopDateChange: isDifferentStopDate,
            hasPendingStopOrderChange: hasStopOrderChange,
          });

          if (isRouteChangeNeeded) {
            setCurrentSlide &&
              setCurrentSlide(EditOrderModalSlides.CHANGE_ROUTE);
          } else {
            onConfirm();
            reset();
          }
        });

      return runSubmission(
        () =>
          fullUpdateOrderMutation({
            variables: {
          id: orderId,
          courierId: courierId,
          caller: data.caller,
          callerPhone: data.callerPhone || null,
          endCustomerId: endCustomerId,
          shipmentUpdates: [
            {
              id: shipment.id,
              shipmentDate,
              ...packageUpdate,
              vehicleTypeId: data.vehicleTypeId,
              stops: submissionStops,
              type: data.type,
              stopIdsToDelete: stopIdsToDeleteOnSubmit,
              stopsToCreate:
                copyOfStopsToCreate.length > 0
                  ? copyOfStopsToCreate
                  : undefined,
              packageDescription: data.packageDescription,
            },
          ],
          endCustomerReferenceNumber: data.endCustomerReferenceNumber,
          serviceTypeId: serviceTypeid || undefined,
          costCenterId: costCenterId || undefined,
          customFieldValuesToReplace: customFieldValues || undefined,
          updatedBy: employeeId ? employeeId : courieUser!.id,
          requiredDocumentTypes: data.requiredDocumentTypes,
          warnedDocumentTypes: data.warnedDocumentTypes,
          additionalEmailRecipients: Array.isArray(
            data.additionalEmailRecipients,
          )
            ? data.additionalEmailRecipients
            : data.additionalEmailRecipients
            ? data.additionalEmailRecipients
                .split(",")
                .map((email) => email.trim())
            : undefined,
            },
          }).then(() => {
            // Confirm Route needs the ids of the stops this save created. If
            // that refetch fails, retry only this continuation—not the mutation.
            rememberContinuation(completePostMutation, data);
            return completePostMutation();
          })
      );
    }
  };

  return {
    onSubmit,
    loading: loading || isSubmitting,
    stopIdsToDelete,
    setStopIdsToDelete,
  };
};

export default useEditOrderFormSubmit;
